import React, { FC } from 'react';
import { AttachmentCard, Attachments } from 'types/Attachments';
import { Field } from '@kontentino/ui';
import { useTranslation } from 'react-i18next';
import EditableLinkCard from 'app/components/linkAttachments/components/editableLinkCards/EditableLinkCard';
import { RunningUpload } from 'types/RunningUpload';
import EditableLinkCardSkeleton from './editableLinkCards/EditableLinkCardSkeleton';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import clsx from 'clsx';
import { lockAxisInTransformStyle } from 'app/modules/posts/utils/lockTransformAxisStyle';

type Props = {
  page: {
    id: number;
    type: number;
  };
  attachmentsCards: Attachments['cards'];
  updateAttachmentsCard(card: Partial<AttachmentCard>, index?: number): void;
  removeAttachmentCard(index: number): void;
  moveAttachmentIndex(oldIndex: number, newIndex: number): void;
  isEditable?: boolean;
  isImageRemovable?: boolean;
  withDescriptionField?: boolean;
  runningUploads: RunningUpload[];
  isFullLinkEditable?: boolean;
};

const EditableLinkCards: FC<Props> = (props) => {
  const { t } = useTranslation();
  function onDragEnd(result: DropResult) {
    if (result.destination) {
      props.moveAttachmentIndex(result.source.index, result.destination.index);
    }
  }

  return (
    <Field.Group>
      <Field.Label required>
        {t('card', { count: props.attachmentsCards.length })}
      </Field.Label>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable" direction="vertical">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              className={clsx('tw-flex tw-flex-col tw-gap-2')}
              data-cy="post-composer_carousel-card"
              {...provided.droppableProps}
            >
              {props.attachmentsCards.map((attachment, index) => (
                <Draggable
                  draggableId={`draggable-editable-link-card-${index}`}
                  key={`draggable-editable-link-card-${index}`}
                  index={index}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={lockAxisInTransformStyle(
                        'Y',
                        provided.draggableProps.style,
                      )}
                    >
                      <EditableLinkCard
                        key={`editable-link-card-${index}`}
                        page={props.page}
                        attachmentCardIndex={
                          props.attachmentsCards.length > 1 ? index : undefined
                        }
                        attachmentCard={attachment}
                        onAttachmentCardChange={(card) =>
                          props.updateAttachmentsCard(card, index)
                        }
                        onAttachmentCardRemove={() =>
                          props.removeAttachmentCard(index)
                        }
                        isEditable={props.isEditable}
                        isImageRemovable={props.isImageRemovable}
                        withDescriptionField={props.withDescriptionField}
                        isFullLinkEditable={props.isFullLinkEditable}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {props.runningUploads.map((attachment, index) => (
        <EditableLinkCardSkeleton
          key={`editable-link-card-skeleton-${index}`}
          src={attachment.base64Preview}
          loadingProgress={attachment.progress}
          orderIndex={props.attachmentsCards.length + index + 1}
          withDescriptionField={props.withDescriptionField}
        />
      ))}
    </Field.Group>
  );
};

export default EditableLinkCards;
